/**
 * searchHighlight.ts
 *
 * Utility per evidenziare a posteriori, nel DOM già renderizzato, le occorrenze
 * di un termine di ricerca. Avvolge i match in <mark class="search-hit">.
 *
 * Supporta opzioni:
 *  - caseSensitive: distingue maiuscole/minuscole
 *  - wholeWord    : limita ai bordi parola (Unicode-aware in modalità letterale)
 *  - regex        : interpreta `term` come pattern regex
 *
 * Sicuro: opera solo su nodi di testo (mai su attributi o tag), e salta
 * <script>, <style>, <textarea> e nodi già evidenziati.
 */

const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "MARK", "NOSCRIPT", "TEXTAREA"]);

export interface HighlightOptions {
  caseSensitive?: boolean;
  wholeWord?: boolean;
  regex?: boolean;
}

/** Escapa i caratteri speciali per inserire la stringa in una RegExp. */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Costruisce la RegExp di ricerca in base alle opzioni.
 * - In modalità letterale + wholeWord usiamo lookaround Unicode così da
 *   gestire correttamente lettere accentate italiane.
 * - In modalità regex + wholeWord usiamo i confini ASCII `\b` per evitare di
 *   invalidare pattern dell'utente che potrebbero non essere /u-compatibili.
 */
function buildRegex(term: string, opts: HighlightOptions): RegExp | null {
  const t = term;
  if (!t) return null;
  const flags = (opts.caseSensitive ? "g" : "gi");
  try {
    if (opts.regex) {
      if (opts.wholeWord) {
        return new RegExp(`(?:^|\\b)(?:${t})(?:\\b|$)`, flags);
      }
      return new RegExp(t, flags);
    }
    const esc = escapeRegex(t);
    if (opts.wholeWord) {
      return new RegExp(`(?<![\\p{L}\\p{N}_])(?:${esc})(?![\\p{L}\\p{N}_])`, flags + "u");
    }
    return new RegExp(esc, flags);
  } catch {
    return null;
  }
}

/**
 * Rimuove tutte le evidenziazioni precedenti dentro `root` (o nell'intero
 * documento se omesso), unendo i nodi di testo risultanti.
 */
export function clearHighlights(root?: HTMLElement | Document | null): number {
  const scope: ParentNode = (root as ParentNode) ?? document;
  const marks = Array.from(scope.querySelectorAll<HTMLElement>("mark.search-hit"));
  for (const m of marks) {
    const parent = m.parentNode;
    if (!parent) continue;
    while (m.firstChild) parent.insertBefore(m.firstChild, m);
    parent.removeChild(m);
    // Unisce eventuali Text nodes adiacenti per pulizia
    (parent as Element).normalize?.();
  }
  return marks.length;
}

/**
 * Evidenzia tutte le occorrenze di `term` all'interno del `root` indicato.
 * Restituisce il numero di match trovati. Non scorre automaticamente; la
 * navigazione tra i match è gestita dal componente {@link SearchHighlightBar}.
 */
export function highlightTerm(
  root: HTMLElement | null,
  term: string,
  opts: HighlightOptions = {},
): number {
  if (!root || !term || term.trim().length < 2) return 0;
  // Pulisce eventuali evidenziazioni residue prima di rifare il lavoro.
  clearHighlights(root);
  const regex = buildRegex(term.trim(), opts);
  if (!regex) return 0;

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      const parent = node.parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      if (SKIP_TAGS.has(parent.tagName)) return NodeFilter.FILTER_REJECT;
      if (!node.nodeValue) return NodeFilter.FILTER_REJECT;
      regex.lastIndex = 0;
      const ok = regex.test(node.nodeValue);
      regex.lastIndex = 0;
      return ok ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
    },
  });

  const targets: Text[] = [];
  let n: Node | null;
  while ((n = walker.nextNode())) targets.push(n as Text);

  let count = 0;

  for (const textNode of targets) {
    const parent = textNode.parentNode;
    if (!parent) continue;
    const text = textNode.nodeValue ?? "";
    const frag = document.createDocumentFragment();
    let lastIdx = 0;
    regex.lastIndex = 0;
    let m: RegExpExecArray | null;
    let safety = 0;
    while ((m = regex.exec(text))) {
      // Protezione contro pattern a lunghezza zero (es. /(?=a)/) che andrebbero
      // in loop infinito.
      if (m[0].length === 0) {
        regex.lastIndex++;
        if (++safety > 5000) break;
        continue;
      }
      if (m.index > lastIdx) frag.appendChild(document.createTextNode(text.slice(lastIdx, m.index)));
      const mark = document.createElement("mark");
      mark.className = "search-hit bg-yellow-200 text-foreground rounded px-0.5";
      mark.textContent = m[0];
      frag.appendChild(mark);
      lastIdx = m.index + m[0].length;
      count++;
      if (++safety > 5000) break;
    }
    if (lastIdx < text.length) frag.appendChild(document.createTextNode(text.slice(lastIdx)));
    parent.replaceChild(frag, textNode);
  }

  return count;
}
